import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router";
import { Card, CircularProgress, Stack } from "@mui/material";
import { OccupancyMetadata } from "./OccupancyMetadata";
import { PolicyDetails } from "./PolicyDetails";
import { PrivateInsuranceDetails } from "./PrivateInsuranceDetails";
import occupiedUnitsDetailsService from "../../../services/occupiedUnitsDetails.service";
import { UnitStatusLabels } from "@safelease/service-utilities";

type LeaseMetadataCardProps = {};

function LeaseMetadataCard({}: LeaseMetadataCardProps) {
  const { leaseId } = useParams<{ leaseId: string }>();
  if (!leaseId) return null;

  const policyDetailsQuery = useQuery({
    queryKey: ["getPolicyDetails", leaseId],
    queryFn: async () => {
      const response = await occupiedUnitsDetailsService.getPolicyDetailsForLease(leaseId);
      return response.data;
    },
  });
  const leaseDetailsQuery = useQuery({
    queryKey: ["getLeaseDetails", leaseId],
    queryFn: async () => {
      const response = await occupiedUnitsDetailsService.getDetailsForLease(leaseId);
      return response.data;
    },
  });
  const privateInsuranceDetailsQuery = useQuery({
    queryKey: ["getPrivateInsuranceDetails", leaseId],
    queryFn: async () => {
      const response = await occupiedUnitsDetailsService.getPrivateInsuranceDetailsForLease(leaseId);
      return response.data;
    },
  });
  const queriesLoading = policyDetailsQuery.isLoading || leaseDetailsQuery.isLoading || privateInsuranceDetailsQuery.isLoading;

  return (
    <Card>
      <Stack sx={{ padding: 2.5 }} spacing={2}>
        {queriesLoading ? (
          <CircularProgress />
        ) : (
          <>
            <OccupancyMetadata leaseDetailsQuery={leaseDetailsQuery} />
            <PolicyDetails
              policyDetailsQuery={policyDetailsQuery}
              showCancelDate={
                leaseDetailsQuery.data?.status === UnitStatusLabels.OPTED_OUT ||
                (policyDetailsQuery.data?.policyEndDate !== null &&
                  policyDetailsQuery.data?.policyEndDate !== undefined &&
                  leaseDetailsQuery.data?.status === UnitStatusLabels.INELIGIBLE)
              } // Only show the cancelDate if the unit is opted out or the policy has a cancel date and the unit is ineligible
            />
            <PrivateInsuranceDetails privateInsuranceDetailsQuery={privateInsuranceDetailsQuery} leaseId={leaseId} />
          </>
        )}
      </Stack>
    </Card>
  );
}

export { LeaseMetadataCard };
